import MoreVertIcon from "@mui/icons-material/MoreVert";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";

import { ThemeProvider } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { globalColors } from "../../Utils/colors";
import { CardContainerProps, TemplateCardContainerProps } from "./type";

import {
  CardHeaderIconsContainer,
  CardHeaderParentContainer,
  CardHeaderTextContainer,
  CardMediaParentContainer,
  CardMediaTextContainer,
  StyledCardContainer,
  getCardContainerTheme,
} from "./CardContainerStyled";
import { AltImage } from "./styled";

const useStyles = makeStyles({
  radio: {
    "&$checked": {
      color: globalColors.primary,
    },
  },
  checked: {},
  lazyImage: {
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  lazyImageLoaded: {
    opacity: 1,
  },
  imagePlaceholder: {
    backgroundColor: '#e0e0e0',
    background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 1.5s infinite',
    height: 200,
    width: '100%',
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
});

// Lazy loading image component with fade-in effect
function LazyCardImage({ src, alt, height }: { src: string; alt: string; height: number }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const classes = useStyles();

  if (hasError || !src) {
    return <div className={classes.imagePlaceholder} style={{ height }} />;
  }

  return (
    <div style={{ position: 'relative', height, overflow: 'hidden' }}>
      {/* Shimmer placeholder - shown until image loads */}
      <div
        className={classes.imagePlaceholder}
        style={{
          height,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          opacity: isLoaded ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
      {/* Actual image with lazy loading */}
      <CardMedia
        alt={alt}
        component="img"
        height={height}
        image={src}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        style={{
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
    </div>
  );
}

export function TemplateCardContainer({
  indexKey,
  cardLastEditedOn,
  cardImage,
  cardTitle,
  isSelected,
  selectHandler,
}: TemplateCardContainerProps) {
  const classes = useStyles();

  const renderTitleContainer = React.useMemo(() => {
    return (
      <CardHeaderParentContainer>
        <CardHeaderTextContainer multiDelete={false}>
          {cardTitle}
        </CardHeaderTextContainer>
        <CardHeaderIconsContainer>
          <Radio
            disableRipple
            checked={isSelected}
            onChange={selectHandler}
            classes={{ root: classes.radio, checked: classes.checked }}
          />
        </CardHeaderIconsContainer>
      </CardHeaderParentContainer>
    );
  }, [cardTitle, classes.checked, classes.radio, isSelected, selectHandler]);

  return (
    <ThemeProvider theme={getCardContainerTheme()}>
      <StyledCardContainer
        key={indexKey}
        multiDelete={false}
        isSelected={isSelected}
        onClick={selectHandler}
      >
        <CardHeader disableTypography title={renderTitleContainer} />
        <CardMediaParentContainer>
          <CardMedia alt="" component="img" height="200" image={cardImage} />
        </CardMediaParentContainer>
        <CardContent>
          <Typography variant="editedOn">{cardLastEditedOn}</Typography>
        </CardContent>
      </StyledCardContainer>
    </ThemeProvider>
  );
}

export default function CardContainer({
  indexKey,
  cardLastEditedOn,
  cardImage,
  cardTitle,
  doubleClickHandler,
  multiDelete,
  isSelected,
  selectHandler,
  setOpen,
  setAnchorEl,
  setElementIndex,
  cardColor
}: CardContainerProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const renderTitleContainer = React.useMemo(() => {
    return (
      <CardHeaderParentContainer>
        <CardHeaderTextContainer multiDelete={multiDelete}>
          {cardTitle}
        </CardHeaderTextContainer>
        <CardHeaderIconsContainer>
          {multiDelete && (
            <Checkbox checked={isSelected} onChange={selectHandler} />
          )}
          <IconButton
            disabled={isSelected}
            onClick={(event) => {
              setElementIndex(indexKey);
              setAnchorEl(event.currentTarget);
              setOpen(true);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </CardHeaderIconsContainer>
      </CardHeaderParentContainer>
    );
  }, [
    cardTitle,
    indexKey,
    isSelected,
    multiDelete,
    selectHandler,
    setAnchorEl,
    setElementIndex,
    setOpen,
  ]);

  useEffect(() => {
    const adjustFontSize = () => {
      const container = containerRef.current;
      const text = textRef.current;

      if (!container || !text) return;

      let fontSize = 20; // Start with a large font size
      text.style.fontSize = `${fontSize}px`;

      // Reduce font size until text fits within the container
      while (
        text.offsetWidth > container.offsetWidth ||
        text.offsetHeight > container.offsetHeight
      ) {
        fontSize -= 1;
        text.style.fontSize = `${fontSize}px`;

        if (fontSize < 1) break; // Safety break
      }
    };

    adjustFontSize();
    window.addEventListener("resize", adjustFontSize);

    return () => {
      window.removeEventListener("resize", adjustFontSize);
    };
  }, [cardTitle]);

  return (
    <ThemeProvider theme={getCardContainerTheme()}>
      <StyledCardContainer
        key={indexKey}
        isSelected={isSelected}
        multiDelete={multiDelete}
        onClick={() => {
          multiDelete ? selectHandler() : setElementIndex(indexKey);
        }}
      >
        <CardHeader disableTypography title={renderTitleContainer} />
        <CardMediaParentContainer
          title={cardTitle}
          onClick={() => {
            if (!multiDelete) {
              setElementIndex(indexKey)
              doubleClickHandler();
            } else {
              selectHandler()
            }
          }}>
          {cardImage ?
            <LazyCardImage
              src={cardImage}
              alt={cardTitle || ''}
              height={200}
            /> :
            <AltImage style={{ backgroundColor: cardColor }} ></AltImage>
          }
          <CardMediaTextContainer
            ref={containerRef}
            onClick={() => {
              if (!multiDelete) {
                setElementIndex(indexKey)
                doubleClickHandler();
              } else {
                selectHandler()
              }
            }}
          ><span ref={textRef}>{cardTitle}</span></CardMediaTextContainer>
        </CardMediaParentContainer>
        <CardContent>
          <Typography variant="editedOn">{cardLastEditedOn}</Typography>
        </CardContent>
      </StyledCardContainer>
    </ThemeProvider>
  );
}
